import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import WalkthroughTooltip from './WalkthroughTooltip';
import { useOnboarding } from '../hooks/useOnboarding';

const WalkthroughManager = ({ isDarkMode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    shouldShowStep,
    completeStep,
    skipWalkthrough,
    STEPS,
    currentStep,
    isComplete,
    onboardingStatus,
    loading,
    forceReload
  } = useOnboarding();

  // Local state to track temporarily dismissed tooltips
  const [dismissedSteps, setDismissedSteps] = React.useState(new Set());
  
  // State to track if AddToVault section is expanded
  const [isAddToVaultExpanded, setIsAddToVaultExpanded] = React.useState(false);
  
  // State to track deployment sub-steps
  const [deploymentSubStep, setDeploymentSubStep] = React.useState(1);
  
  // State to track chat features sub-steps
  const [chatSubStep, setChatSubStep] = React.useState(1);
  
  // State to track if personality section is expanded
  const [isPersonalityExpanded, setIsPersonalityExpanded] = React.useState(false);

  // Check for onboarding reset flag whenever location changes
  React.useEffect(() => {
    const wasReset = sessionStorage.getItem('onboardingReset');
    if (wasReset) {
      sessionStorage.removeItem('onboardingReset');
      forceReload();
    }
  }, [location.pathname, forceReload]);

  // Clear dismissed steps when location changes (so tooltips can appear on new pages)
  React.useEffect(() => {
    setDismissedSteps(new Set());
    // Reset deployment sub-step when navigating away from deployments
    if (!location.pathname.includes('/deployments')) {
      setDeploymentSubStep(1);
    }
    // Reset chat sub-step when navigating away from dashboard
    if (!location.pathname.includes('/dashboard')) {
      setChatSubStep(1);
      setIsPersonalityExpanded(false);
    }
  }, [location.pathname]);

  // Effect to track AddToVault section expansion
  React.useEffect(() => {
    const checkExpansion = () => {
      // Look for the textarea that appears when Add to Vault is expanded
      const textarea = document.querySelector('textarea[placeholder*="Type or paste something"]');
      const isExpanded = !!textarea;
      
      if (isExpanded !== isAddToVaultExpanded) {
        setIsAddToVaultExpanded(isExpanded);
      }
    };
    
    // Check immediately
    checkExpansion();
    
    // Set up a mutation observer to watch for DOM changes
    const observer = new MutationObserver(() => {
      // Add a small delay to let DOM settle
      setTimeout(checkExpansion, 10);
    });
    
    observer.observe(document.body, { 
      childList: true, 
      subtree: true,
      attributes: true
    });
    
    return () => observer.disconnect();
  }, [location.pathname, isAddToVaultExpanded]); // Re-run when location changes

  // Effect to track personality section expansion
  React.useEffect(() => {
    const checkPersonalityExpansion = () => {
      // Look for the personality textarea that appears when Edit Personality is clicked
      const personalityTextarea = document.querySelector('textarea[placeholder*="helpful AI"], button[style*="margin-top: 30px"].btn-dark');
      const isExpanded = !!personalityTextarea;
      
      if (isExpanded !== isPersonalityExpanded) {
        setIsPersonalityExpanded(isExpanded);
      }
    };
    
    // Check immediately
    checkPersonalityExpansion();
    
    // Set up a mutation observer to watch for DOM changes
    const observer = new MutationObserver(() => {
      // Add a small delay to let DOM settle
      setTimeout(checkPersonalityExpansion, 10);
    });
    
    observer.observe(document.body, { 
      childList: true, 
      subtree: true,
      attributes: true
    });
    
    return () => observer.disconnect();
  }, [location.pathname, isPersonalityExpanded]); // Re-run when location changes

  // Don't show walkthrough if it's complete
  if (isComplete()) return null;

  const handleNext = async (stepNumber, nextAction) => {
    try {
      const result = await completeStep(stepNumber);
      
      if (nextAction) {
        nextAction();
      }
    } catch (error) {
      console.error(`❌ Error completing step ${stepNumber}:`, error);
      // Show user-friendly error message
      alert(`Error completing step: ${error.message || 'Please try again'}`);
    }
  };

  const handleSkip = async () => {
    try {
      await skipWalkthrough();
    } catch (error) {
      console.error('Error skipping walkthrough:', error);
    }
  };

  const handleClose = (stepNumber) => {
    // Just temporarily dismiss this tooltip - don't skip the tutorial
    setDismissedSteps(prev => new Set([...prev, stepNumber]));
  };

  const handleBack = () => {
    // Handle back navigation based on current step and sub-steps
    if (currentStep === STEPS.TALK_TO_VAULT && chatSubStep > 1) {
      // Navigate back within chat sub-steps
      setChatSubStep(chatSubStep - 1);
    } else if (currentStep === STEPS.MAKE_DEPLOYMENT && deploymentSubStep > 1) {
      // Navigate back within deployment sub-steps
      setDeploymentSubStep(deploymentSubStep - 1);
    } else if (currentStep === STEPS.ADD_OPENAI_KEY && location.pathname.includes('/settings')) {
      // Go back from settings to dashboard
      navigate('/dashboard');
    } else if (currentStep === STEPS.ADD_DATA_TO_VAULT && location.pathname.includes('/dashboard')) {
      // Go back from dashboard to settings
      navigate('/settings');
    } else if (currentStep === STEPS.MAKE_DEPLOYMENT && location.pathname.includes('/dashboard')) {
      // Reset chat sub-step to last step when going back to chat exploration
      setChatSubStep(8);
    } else if (currentStep === STEPS.MAKE_FLOW && location.pathname.includes('/deployments')) {
      // Reset deployment sub-step to last step when going back to deployment
      setDeploymentSubStep(5);
    } else if (currentStep === STEPS.MAKE_FLOW && location.pathname.includes('/vector-flow')) {
      // Go back to deployments page
      navigate('/deployments');
    }
  };

  // Helper function to check if a step should be shown (not dismissed)
  const shouldShowStepAndNotDismissed = (stepNumber) => {
    return shouldShowStep(stepNumber) && !dismissedSteps.has(stepNumber);
  };

  // Step 1: Create a new vault (Dashboard)
  if (shouldShowStepAndNotDismissed(STEPS.CREATE_VAULT) && (location.pathname.includes('/dashboard'))) {
    return (
      <WalkthroughTooltip
        targetSelector="select.sleek-select-dark, select.sleek-select-light, input[placeholder='Enter new vault name']"
        title="Create a brand new Vault"
        content="Welcome to Vector Vault! Let's start by creating your first vault. Click on the dropdown below, then select 'Create New' to create your vault."
        description="A vault is our term for an independent vector database where you'll store and search your data."
        stepNumber={1}
        totalSteps={8}
        onNext={() => handleNext(STEPS.CREATE_VAULT)}
        onSkip={handleSkip}
        onClose={() => handleClose(STEPS.CREATE_VAULT)}
        onBack={handleBack}
        isDarkMode={isDarkMode}
        actionText="I've created my vault"
        showBack={false}
      />
    );
  }

  // Step 2: Navigate to API keys after vault creation
  if (shouldShowStepAndNotDismissed(STEPS.ADD_OPENAI_KEY) && (location.pathname.includes('/dashboard'))) {
    return (
      <WalkthroughTooltip
        targetSelector=".sidebar li:last-child, .sidebar ul li:last-child, h3"
        title="Great! Now let's get AI activated"
        content="Perfect! You've created your vault. Now we need to add AI capabilities. Let's head over to the Settings page to add your OpenAI key."
        stepNumber={2}
        totalSteps={8}
        onNext={() => {
          // Don't complete the step yet - just navigate
          navigate('/settings');
        }}
        onSkip={handleSkip}
        onClose={() => handleClose(STEPS.ADD_OPENAI_KEY)}
        onBack={handleBack}
        isDarkMode={isDarkMode}
        actionText="Go to Settings"
      />
    );
  }

  // Step 2: Add OpenAI API key (Settings page)
  if (shouldShowStepAndNotDismissed(STEPS.ADD_OPENAI_KEY) && location.pathname.includes('/settings')) {
    return (
      <WalkthroughTooltip
        targetSelector="h3 + div input[type='text'], h3 ~ div input[type='text']:first-of-type, form div:nth-of-type(4) input[type='text']"
        title="Add your OpenAI API Key"
        content={
          <>
            You need to add your OpenAI key to get vector embeddings and chat functionality. Paste your OpenAI API key here, then click Save.
            <br /><br />
            <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" style={{color: '#667eea', textDecoration: 'underline'}}>
              Get your OpenAI API key here
            </a>
          </>
        }
        description="You can also add Anthropic keys and other AI model keys if you have them to use those models."
        stepNumber={2}
        totalSteps={8}
        onNext={() => handleNext(STEPS.ADD_OPENAI_KEY)}
        onSkip={handleSkip}
        onClose={() => handleClose(STEPS.ADD_OPENAI_KEY)}
        onBack={handleBack}
        isDarkMode={isDarkMode}
        actionText="I've added my key"
      />
    );
  }

  // Step 3: Navigate back to dashboard after adding API key
  if (shouldShowStepAndNotDismissed(STEPS.ADD_DATA_TO_VAULT) && location.pathname.includes('/settings')) {
    return (
      <WalkthroughTooltip
        targetSelector=".sidebar li:first-child, .sidebar ul li:first-child, h3"
        title="Excellent! Let's add some data"
        content="Great! Your API key is saved. Now let's head back to the dashboard to add some data to your vault."
        stepNumber={3}
        totalSteps={8}
        onNext={() => {
          // Don't complete the step yet - just navigate
          navigate('/dashboard');
        }}
        onSkip={handleSkip}
        onClose={() => handleClose(STEPS.ADD_DATA_TO_VAULT)}
        onBack={handleBack}
        isDarkMode={isDarkMode}
        actionText="Go to Dashboard"
      />
    );
  }

  // Step 3: Add data to vault (Dashboard)
  if (shouldShowStepAndNotDismissed(STEPS.ADD_DATA_TO_VAULT) && (location.pathname.includes('/dashboard'))) {
    const forcePosition = isAddToVaultExpanded ? "right-side" : undefined;
    
    // Different content based on whether section is expanded
    const content = isAddToVaultExpanded 
      ? "Perfect! Now you can add your data. Paste text, upload files, or add a webpage by URL."
      : "Now let's add some data! Click the 'Add to Vault' button to expand the data input section.";
    
    const description = isAddToVaultExpanded 
      ? (
          <>
            If you don't have any data ready, try copying a news article from{' '}
            <a href="https://yahoo.com/news" target="_blank" rel="noopener noreferrer" style={{color: '#667eea', textDecoration: 'underline'}}>
              yahoo.com/news
            </a>
            {' '}to experiment with.
          </>
        )
      : null;
    
    return (
      <WalkthroughTooltip
        key={`step3-${isAddToVaultExpanded}`} // Force re-render when expansion changes
        targetSelector="button.link-button, .link-button, button[style*='text-decoration: underline']"
        title="Add data to your vault"
        content={content}
        description={description}
        stepNumber={3}
        totalSteps={8}
        onNext={() => handleNext(STEPS.ADD_DATA_TO_VAULT)}
        onSkip={handleSkip}
        onClose={() => handleClose(STEPS.ADD_DATA_TO_VAULT)}
        onBack={handleBack}
        isDarkMode={isDarkMode}
        actionText="I've added data"
        forcePosition={forcePosition}
      />
    );
  }

  // Step 4: Chat features exploration (Dashboard)
  if (shouldShowStepAndNotDismissed(STEPS.TALK_TO_VAULT) && (location.pathname.includes('/dashboard'))) {
    
    if (chatSubStep === 1) {
      // Step 4a: Initial chat test
      return (
        <WalkthroughTooltip
          targetSelector="textarea[placeholder='Type your message here...'][rows='5'], textarea[rows='5']:not([placeholder*='helpful']), .chat-input textarea"
          title="Test your AI!"
          content="Excellent! Now let's see your AI in action. Ask a question about the data you just added to your vault and watch your AI provide intelligent responses."
          description="Try questions like 'What is this about?' or 'Summarize the main points' to see how your AI understands your data."
          stepNumber={4}
          totalSteps={8}
          onNext={() => setChatSubStep(2)}
          onSkip={handleSkip}
          onClose={() => handleClose(STEPS.TALK_TO_VAULT)}
          onBack={handleBack}
          isDarkMode={isDarkMode}
          actionText="I've chatted with my AI"
        />
      );
    } else if (chatSubStep === 2) {
      // Step 4b: Edit personality
      const content = isPersonalityExpanded 
        ? "Perfect! Now you can customize your AI's personality. The textarea is pre-filled with a default prompt - you can modify it or replace it entirely."
        : "Great! Now let's make your AI more interesting. Click the 'Edit Personality' button to give your AI a unique voice and style.";
      
      const actionText = isPersonalityExpanded ? "Continue" : "Continue";
      
      return (
        <WalkthroughTooltip
          key={`step4b-${isPersonalityExpanded}`} // Force re-render when expansion changes
          targetSelector="button.btn-dark[style*='margin-top: 30px'], button.btn-dark, .btn-dark"
          title="Customize your AI's personality"
          content={content}
          stepNumber={4}
          totalSteps={8}
          onNext={() => setChatSubStep(3)}
          onSkip={handleSkip}
          onClose={() => handleClose(STEPS.TALK_TO_VAULT)}
          onBack={handleBack}
          isDarkMode={isDarkMode}
          actionText={actionText}
          showSkip={false}
        />
      );
    } else if (chatSubStep === 3) {
      // Step 4c: Add personality prompt
      return (
        <WalkthroughTooltip
          targetSelector="textarea[placeholder*='helpful AI'], textarea[rows='1'], button.btn-dark + textarea"
          title="Add a fun personality"
          content="In the personality field, try adding: 'Talk like Snoop Dogg' - then click Save. This will give your AI a distinctive speaking style!"
          stepNumber={4}
          totalSteps={8}
          onNext={() => setChatSubStep(4)}
          onSkip={handleSkip}
          onClose={() => handleClose(STEPS.TALK_TO_VAULT)}
          onBack={handleBack}
          isDarkMode={isDarkMode}
          actionText="OK I've done that"
          showSkip={false}
        />
      );
    } else if (chatSubStep === 4) {
      // Step 4d: Chat again with personality
      return (
        <WalkthroughTooltip
          key={`step4d-${isPersonalityExpanded}`}
          targetSelector="textarea[placeholder='Type your message here...']:not([rows='1']), textarea:not([placeholder*='helpful']):not([rows='1'])"
          title="Try your new personality!"
          content="Perfect! Now try chatting with your AI again and see how it sounds with the new Snoop Dogg personality. Ask the same question as before!"
          stepNumber={4}
          totalSteps={8}
          onNext={() => setChatSubStep(5)}
          onSkip={handleSkip}
          onClose={() => handleClose(STEPS.TALK_TO_VAULT)}
          onBack={handleBack}
          isDarkMode={isDarkMode}
          actionText="I've tried the new personality"
          showSkip={false}
        />
      );
    } else if (chatSubStep === 5) {
      // Step 4e: Select AI model
      return (
        <WalkthroughTooltip
          targetSelector="select.sleek-select-dark[style*='margin-left: 10px'], select.sleek-select-dark[style*='margin-top: 20px'], select.sleek-select-dark:nth-of-type(2)"
          title="Choose your AI model"
          content="Amazing! Now let's explore model selection. You can choose from different AI models - we keep our selection up to date as new models are released. Feel free to pick any model you like!"
          stepNumber={4}
          totalSteps={8}
          onNext={() => setChatSubStep(6)}
          onSkip={handleSkip}
          onClose={() => handleClose(STEPS.TALK_TO_VAULT)}
          onBack={handleBack}
          isDarkMode={isDarkMode}
          actionText="I've selected a model"
          showSkip={false}
        />
      );
    } else if (chatSubStep === 6) {
      // Step 4f: Click advanced options arrow
      return (
        <WalkthroughTooltip
          targetSelector="span[style*='transform: rotate'], span[style*='margin-left: 4px'][style*='margin-right: 6px'], .arrow"
          title="Explore advanced options"
          content="Great! Now click on this arrow (▶) to reveal advanced chat controls that let you fine-tune how your AI works."
          stepNumber={4}
          totalSteps={8}
          onNext={() => setChatSubStep(7)}
          onSkip={handleSkip}
          onClose={() => handleClose(STEPS.TALK_TO_VAULT)}
          onBack={handleBack}
          isDarkMode={isDarkMode}
          actionText="I've opened the options"
          showSkip={false}
        />
      );
    } else if (chatSubStep === 7) {
      // Step 4g: Uncheck get context
      return (
        <WalkthroughTooltip
          targetSelector="input#vault-reply, input[id='vault-reply'], input[type='checkbox'][checked]"
          title="Try without your data"
          content="Perfect! Now uncheck 'get context' to see the difference. When checked, your AI uses your vault data (RAG). When unchecked, it's like using the regular model without your custom data."
          stepNumber={4}
          totalSteps={8}
          onNext={() => setChatSubStep(8)}
          onSkip={handleSkip}
          onClose={() => handleClose(STEPS.TALK_TO_VAULT)}
          onBack={handleBack}
          isDarkMode={isDarkMode}
          actionText="I've unchecked it"
          showSkip={false}
        />
      );
    } else if (chatSubStep === 8) {
      // Step 4h: Chat again without RAG
      return (
        <WalkthroughTooltip
          targetSelector="textarea[placeholder='Type your message here...'][rows='5'], textarea[rows='5']:not([placeholder*='helpful']), .chat-input textarea"
          title="Compare the responses"
          content="Excellent! Now try asking the same question again. You'll notice the AI responds differently because it's not using your vault data - it's just the base model. This shows you the power of RAG (Retrieval Augmented Generation)!"
          stepNumber={4}
          totalSteps={8}
          onNext={() => handleNext(STEPS.TALK_TO_VAULT)}
          onSkip={handleSkip}
          onClose={() => handleClose(STEPS.TALK_TO_VAULT)}
          onBack={handleBack}
          isDarkMode={isDarkMode}
          actionText="Ready for deployments!"
        />
      );
    }
  }

  // Step 5: Navigate to deployments
  if (shouldShowStepAndNotDismissed(STEPS.MAKE_DEPLOYMENT) && (location.pathname.includes('/dashboard'))) {
    return (
      <WalkthroughTooltip
        targetSelector=".sidebar a[href*='deployment'], .sidebar li:nth-child(4), .sidebar ul li:nth-child(4), a[href='/deployments']"
        title="Share your AI with others"
        content="Fantastic! Your AI is working perfectly. Now let's create a deployment to turn your AI into a live website that anyone can visit and chat with!"
        stepNumber={5}
        totalSteps={8}
        onNext={() => {
          // Don't complete the step yet - just navigate
          navigate('/deployments');
        }}
        onSkip={handleSkip}
        onClose={() => handleClose(STEPS.MAKE_DEPLOYMENT)}
        onBack={handleBack}
        isDarkMode={isDarkMode}
        actionText="Go to Deployments"
      />
    );
  }



  // Step 5: Deployment process (Deployments page)
  if (shouldShowStepAndNotDismissed(STEPS.MAKE_DEPLOYMENT) && location.pathname.includes('/deployments')) {
    
    if (deploymentSubStep === 1) {
      // Step 5a: Title
      return (
        <WalkthroughTooltip
          targetSelector='input[type="text"]'
          title="Name your AI website"
          content="First, give your deployment a catchy title. This will be the main heading visitors see when they visit your AI website."
          description="Choose something descriptive like 'My News AI Assistant' or 'Customer Support Bot'"
          stepNumber={5}
          totalSteps={8}
          onNext={() => setDeploymentSubStep(2)}
          onSkip={handleSkip}
          onClose={() => handleClose(STEPS.MAKE_DEPLOYMENT)}
          onBack={handleBack}
          isDarkMode={isDarkMode}
          actionText="Continue"
          showSkip={false}
        />
      );
    } else if (deploymentSubStep === 2) {
      // Step 5b: Vault selection
      return (
        <WalkthroughTooltip
          targetSelector='select'
          title="Choose your data source"
          content="Select the vault containing the data you want your AI to use. This should be the vault where you added your data in the previous step."
          stepNumber={5}
          totalSteps={8}
          onNext={() => setDeploymentSubStep(3)}
          onSkip={handleSkip}
          onClose={() => handleClose(STEPS.MAKE_DEPLOYMENT)}
          onBack={handleBack}
          isDarkMode={isDarkMode}
          actionText="Continue"
          showSkip={false}
        />
      );
    } else if (deploymentSubStep === 3) {
      // Step 5c: Enable chat
      return (
        <WalkthroughTooltip
          targetSelector='div[style*="flex-wrap"] div:nth-child(2) input[type="checkbox"], div[style*="gap: 40px"] div:nth-child(2) input[type="checkbox"]'
          title="Enable chat functionality"
          content="Make sure to check the 'Chat' checkbox so visitors can actually chat with your AI! This is the main feature people will use."
          description="Pro tip: You can hover over any checkbox to see what it does"
          stepNumber={5}
          totalSteps={8}
          onNext={() => setDeploymentSubStep(4)}
          onSkip={handleSkip}
          onClose={() => handleClose(STEPS.MAKE_DEPLOYMENT)}
          onBack={handleBack}
          isDarkMode={isDarkMode}
          actionText="Continue"
          showSkip={false}
        />
      );
    } else if (deploymentSubStep === 4) {
      // Step 5d: Deploy button
      return (
        <WalkthroughTooltip
          targetSelector='button.button[type="submit"], form button[type="submit"], button[type="submit"].button'
          title="Launch your AI website!"
          content="Perfect! Now click the Deploy button to create your live AI website. Fill in any remaining fields you want (like description and URL path) then deploy!"
          description="Remember: you can hover over any field or checkbox to see what it does"
          stepNumber={5}
          totalSteps={8}
          onNext={() => setDeploymentSubStep(5)}
          onSkip={handleSkip}
          onClose={() => handleClose(STEPS.MAKE_DEPLOYMENT)}
          onBack={handleBack}
          isDarkMode={isDarkMode}
          actionText="I've deployed my AI"
          forcePosition="top"
        />
      );
    } else if (deploymentSubStep === 5) {
      // Step 6: Post-deployment information
      return (
        <WalkthroughTooltip
          targetSelector="body"
          title="🎉 Deployment successful!"
          content="Congratulations! Your AI website is now being created. Your site will be live and accessible to others in 15-30 minutes as your custom DNS propagates across the internet."
          description="You'll receive the website URL once it's ready. In the meantime, let's explore more advanced features!"
          stepNumber={6}
          totalSteps={8}
          onNext={() => handleNext(STEPS.MAKE_DEPLOYMENT)}
          onSkip={handleSkip}
          onClose={() => handleClose(STEPS.MAKE_DEPLOYMENT)}
          onBack={handleBack}
          isDarkMode={isDarkMode}
          actionText="Continue"
        />
      );
    }
  }

  // Step 7: Navigate to Vector Flow
  if (shouldShowStepAndNotDismissed(STEPS.MAKE_FLOW) && location.pathname.includes('/deployments')) {
    return (
      <WalkthroughTooltip
        targetSelector="body"
        title="Build powerful AI workflows"
        content="Now let's explore Vector Flow - our advanced UI Agent builder where you can create complex AI workflows, automations, and multi-step processes!"
        stepNumber={7}
        totalSteps={8}
        onNext={() => {
          navigate('/vector-flow');
        }}
        onSkip={handleSkip}
        onClose={() => handleClose(STEPS.MAKE_FLOW)}
        onBack={handleBack}
        isDarkMode={isDarkMode}
        actionText="Go to Vector Flow"
      />
    );
  }

  // Step 8: Make a flow (Vector Flow page)
  if (shouldShowStepAndNotDismissed(STEPS.MAKE_FLOW) && location.pathname.includes('/vector-flow')) {
    return (
      <WalkthroughTooltip
        targetSelector=".drawflow, #drawflow, .flow-builder-container, .canvas"
        title="Welcome to Vector Flow!"
        content="This is our powerful UI Agent builder! Here you can create complex AI workflows, connect different AI models, and build sophisticated automations. Explore the interface and start building!"
        stepNumber={8}
        totalSteps={8}
        onNext={() => handleNext(STEPS.MAKE_FLOW)}
        onSkip={handleSkip}
        onClose={() => handleClose(STEPS.MAKE_FLOW)}
        onBack={handleBack}
        isDarkMode={isDarkMode}
        actionText="Complete Tutorial"
        showSkip={false}
      />
    );
  }

  // Return null if no step should be shown on current page
  return null;
};

export default WalkthroughManager; 